import { wrapAngle, type CarSnap, type Snapshot, type Vec2 } from '@nanocars/shared';

const TICK_MS = 1000 / 60;
const RING_SIZE = 32;
const EXTRAPOLATE_MAX_MS = 250;

// EWMA smoothing for the tick-clock-to-local-clock offset, same shape as
// NetClient's serverTickOffset smoothing: snapshots arrive with network
// jitter on receivedAtMs, but server ticks themselves are evenly spaced, so
// anchoring samples to tick*TICK_MS and smoothing out the offset gives a
// steadier timeline to interpolate along than raw receive times would.
const OFFSET_ALPHA = 0.1;

export interface RemoteCarTransform {
  pos: Vec2;
  heading: number;
  air: number;
  el: boolean;
  lap: number;
}

interface Entry {
  s: Snapshot;
  tickMs: number;
}

// Renders every OTHER car (never the locally-predicted one) some fixed
// delay behind the newest snapshot, smoothly interpolated between the two
// snapshots bracketing that delayed instant. Keeps the last RING_SIZE
// snapshots; sample() is a pure function of that buffer plus the requested
// time, so it can be called every render frame without side effects other
// than the offset EWMA update in push().
export class Interpolation {
  private readonly buffer: Entry[] = [];
  private offsetMs: number | null = null;

  push(s: Snapshot, receivedAtMs: number): void {
    const tickMs = s.tick * TICK_MS;
    const sampleOffset = receivedAtMs - tickMs;
    this.offsetMs = this.offsetMs === null ? sampleOffset : this.offsetMs + OFFSET_ALPHA * (sampleOffset - this.offsetMs);

    this.buffer.push({ s, tickMs });
    if (this.buffer.length > RING_SIZE) this.buffer.shift();
  }

  sample(nowMs: number, delayMs: number): Map<number, RemoteCarTransform> {
    const result = new Map<number, RemoteCarTransform>();
    if (this.buffer.length === 0) return result;

    const target = nowMs - delayMs;
    const oldest = this.buffer[0]!;
    const newest = this.buffer[this.buffer.length - 1]!;

    if (target <= this.localTimeOf(oldest)) {
      for (const car of oldest.s.cars) result.set(car.id, holdTransform(car));
      return result;
    }

    if (target >= this.localTimeOf(newest)) {
      const dtS = Math.min(EXTRAPOLATE_MAX_MS, target - this.localTimeOf(newest)) / 1000;
      for (const car of newest.s.cars) result.set(car.id, extrapolateTransform(car, dtS));
      return result;
    }

    let before = oldest;
    let after = newest;
    for (let i = 0; i < this.buffer.length - 1; i++) {
      const cur = this.buffer[i]!;
      const next = this.buffer[i + 1]!;
      if (this.localTimeOf(cur) <= target && target <= this.localTimeOf(next)) {
        before = cur;
        after = next;
        break;
      }
    }

    const ta = this.localTimeOf(before);
    const tb = this.localTimeOf(after);
    const t = tb === ta ? 0 : (target - ta) / (tb - ta);

    const afterById = new Map(after.s.cars.map((c) => [c.id, c] as const));
    for (const carA of before.s.cars) {
      const carB = afterById.get(carA.id) ?? carA;
      result.set(carA.id, lerpTransform(carA, carB, t));
    }
    // A car that only exists in the later snapshot (joined mid-buffer) still
    // needs a transform - fall back to holding its own state.
    for (const carB of after.s.cars) {
      if (!result.has(carB.id)) result.set(carB.id, holdTransform(carB));
    }

    return result;
  }

  private localTimeOf(entry: Entry): number {
    return entry.tickMs + (this.offsetMs ?? 0);
  }
}

function holdTransform(c: CarSnap): RemoteCarTransform {
  return { pos: { x: c.x, y: c.y }, heading: c.h, air: c.air, el: c.el === 1, lap: c.lap };
}

function extrapolateTransform(c: CarSnap, dtS: number): RemoteCarTransform {
  return {
    pos: { x: c.x + c.vx * dtS, y: c.y + c.vy * dtS },
    heading: c.h,
    air: c.air,
    el: c.el === 1,
    lap: c.lap,
  };
}

function lerpTransform(a: CarSnap, b: CarSnap, t: number): RemoteCarTransform {
  return {
    pos: { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t },
    heading: a.h + wrapAngle(b.h - a.h) * t,
    air: a.air + (b.air - a.air) * t,
    el: t < 0.5 ? a.el === 1 : b.el === 1,
    lap: t < 0.5 ? a.lap : b.lap,
  };
}
